/** @jsxImportSource @emotion/react */
import {
  Box,
  CircularProgress,
  FormLabel,
  IconButton,
  MenuItem,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Theme,
  Tooltip,
  useTheme,
} from '@mui/material';
import Heading from 'components/Heading';
import CommonPageLayout from 'components/layouts/CommonPageLayout';
import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { UseStyles } from 'types/styles';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import Select from 'components/Select';
import { useMessages } from 'contexts/message';
import { Alert } from 'components/Alert';
import { useUploadPage } from './useUploadPage';
import { config } from 'application';
import { useTablePagination } from 'hooks/useTablePagination';
import { parseQuerySortingProps } from 'utils/net';
import { SelectGroupSeparator } from 'components/SelectGroupSeparator';
import { renderGroupedTemplatesMenuItems } from 'utils/component';
import ConfirmDialog from 'components/ConfirmDialog';
import { FEATURE_ADMIN_AREA, isEnabled } from 'utils/feature';
import { AuthenticatedTemplate } from '@azure/msal-react';
import { Helmet } from 'react-helmet';
import NationalPlanningProcess from 'pages/UploadsPage/NationalPlanningProcess';

const UploadsPage: React.FC = () => {
  const theme = useTheme() as Theme;
  const classes = useStyles(theme);
  const { messages, clear } = useMessages();
  const { formatDate, formatMessage } = useIntl();
  const {
    plans,
    countries,
    adminAreas,
    templates,
    dataTotalCount,
    searchQuery,
    searchParams,
    isPlansLoading,
    isTemplatesLoading,
    isCountriesLoading,
    isAdminAreasLoading,
    setSearchParams,
    createHandleDownload,
    createHandleDelete,
    changeQuery,
    changeSort,
    getAdminAreaValue,
  } = useUploadPage();
  const tablePagination = useTablePagination({ searchParams, setSearchParams, count: dataTotalCount || 0 });

  const createQuerySelectorHander = (field: string) => (event: SelectChangeEvent<unknown>, _: React.ReactNode) => {
    tablePagination.setPage(0);
    changeQuery(field, event.target.value);
  };

  const createSortHandler = (field: string) => () => {
    changeSort(field);
  };

  useEffect(() => () => clear(), [clear]);

  return (
    <CommonPageLayout>
      <Helmet>
        <title>{formatMessage({ id: 'pageTitle.uploads', defaultMessage: 'Uploads' })}</title>
      </Helmet>
      <AuthenticatedTemplate>
        <NationalPlanningProcess />
        <Box p={8} pt={2}>
          <Box css={classes.header} mb={6}>
            <Heading component="h2" mb={0}>
              <FormattedMessage id={'UploadsPage.allUploads'} defaultMessage={'All Uploads'} />
            </Heading>
          </Box>

          <Box mb={2}>
            {messages.length > 0 && (
              <Alert severity={messages[0].severity} onClose={clear}>
                {messages[0].message}
              </Alert>
            )}
          </Box>

          <Box mb={2} css={classes.filters}>
            <Box display="flex" alignItems="center" gap={2}>
              <FormLabel css={classes.filterLabel}>
                <FormattedMessage id={'UploadsPage.template'} defaultMessage={'Template'} />
              </FormLabel>
              <Select
                value={searchParams.get('typeId') || '-1'}
                onChange={createQuerySelectorHander('typeId')}
                disabled={isTemplatesLoading}
                css={classes.filter}
              >
                <MenuItem value={'-1'}>
                  <FormattedMessage id={'UploadsPage.all'} defaultMessage={'All'} />
                </MenuItem>
                <SelectGroupSeparator />
                {renderGroupedTemplatesMenuItems(templates || [])}
              </Select>
            </Box>
            <Box display="flex" alignItems="center" gap={2}>
              <FormLabel css={classes.filterLabel}>
                <FormattedMessage id={'UploadsPage.country'} defaultMessage={'Country'} />
              </FormLabel>
              <Select
                value={searchParams.get('countryId') || '-1'}
                onChange={createQuerySelectorHander('countryId')}
                disabled={isCountriesLoading}
                css={classes.filter}
              >
                <MenuItem value="-1">
                  <FormattedMessage id={'common.all'} defaultMessage={'All'} />
                </MenuItem>
                {Boolean(countries?.length) && <SelectGroupSeparator />}
                {countries?.map(country => (
                  <MenuItem key={country.id} value={country.id}>
                    {country.name}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            {isEnabled(FEATURE_ADMIN_AREA) && (
              <Box display="flex" alignItems="center" gap={2}>
                <FormLabel css={classes.filterLabel}>
                  <FormattedMessage id={'UploadsPage.administrativeArea'} defaultMessage={'Administrative area'} />
                </FormLabel>
                <Select
                  value={getAdminAreaValue()}
                  onChange={createQuerySelectorHander('adminAreaId')}
                  disabled={isAdminAreasLoading}
                  css={classes.filter}
                >
                  <MenuItem value="-1">
                    <FormattedMessage id={'common.all'} defaultMessage={'All'} />
                  </MenuItem>
                  <SelectGroupSeparator />
                  <MenuItem value="0">
                    <FormattedMessage id={'common.national'} defaultMessage={'National'} />
                  </MenuItem>
                  {Boolean(adminAreas?.length) && <SelectGroupSeparator />}
                  {adminAreas?.map(adminArea => (
                    <MenuItem key={adminArea.id} value={adminArea.id}>
                      {adminArea.name}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            )}
            <Box display="flex" alignItems="center" gap={2}>
              <FormLabel css={classes.filterLabel}>
                <FormattedMessage id={'UploadsPage.show'} defaultMessage={'Show'} />
              </FormLabel>
              <Select value={searchParams.get('latest') || 'true'} onChange={createQuerySelectorHander('latest')}>
                <MenuItem value="false">
                  <FormattedMessage id={'UploadsPage.all'} defaultMessage={'All'} />
                </MenuItem>
                <MenuItem value="true">
                  <FormattedMessage id={'UploadsPage.latestOnly'} defaultMessage={'Latest only'} />
                </MenuItem>
              </Select>
            </Box>
          </Box>

          {isPlansLoading && <CircularProgress />}
          {!isPlansLoading && (
            <Box>
              <Table>
                <colgroup>
                  {/* <col width="1%" /> */}
                  <col />
                  <col />
                  <col />
                  <col />
                  <col width="1%" />
                  <col width="1%" />
                </colgroup>
                <TableHead>
                  <TableRow>
                    {/* <TableCell></TableCell> */}
                    <TableCell>
                      <TableSortLabel
                        {...parseQuerySortingProps('typeName', searchQuery.sorting)}
                        onClick={createSortHandler('typeName')}
                        IconComponent={ArrowDropDownIcon}
                      >
                        <FormattedMessage id={'UploadsPage.template'} defaultMessage={'Template'} />
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        {...parseQuerySortingProps('countryName', searchQuery.sorting)}
                        onClick={createSortHandler('countryName')}
                        IconComponent={ArrowDropDownIcon}
                      >
                        <FormattedMessage id={'UploadsPage.country'} defaultMessage={'Country'} />
                      </TableSortLabel>
                    </TableCell>
                    {isEnabled(FEATURE_ADMIN_AREA) && (
                      <TableCell>
                        <TableSortLabel
                          {...parseQuerySortingProps('adminAreaName', searchQuery.sorting)}
                          onClick={createSortHandler('adminAreaName')}
                          IconComponent={ArrowDropDownIcon}
                        >
                          <FormattedMessage
                            id={'UploadsPage.administrativeArea'}
                            defaultMessage={'Administrative area'}
                          />
                        </TableSortLabel>
                      </TableCell>
                    )}
                    <TableCell>
                      <TableSortLabel
                        {...parseQuerySortingProps('createdDate', searchQuery.sorting)}
                        onClick={createSortHandler('createdDate')}
                        IconComponent={ArrowDropDownIcon}
                      >
                        <FormattedMessage id={'UploadsPage.date'} defaultMessage={'Date'} />
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <FormattedMessage id={'common.author'} defaultMessage={'Author'} />
                    </TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {plans?.map(plan => (
                    <TableRow key={plan.id}>
                      {/* <TableCell>
                        <TemplatesIcon code={plan.type.code} color="primary" />
                      </TableCell> */}
                      <TableCell>{plan.type.name}</TableCell>
                      <TableCell>{plan.country.name}</TableCell>
                      {isEnabled(FEATURE_ADMIN_AREA) && (
                        <TableCell>
                          {plan.adminArea?.name || (
                            <FormattedMessage id={'common.national'} defaultMessage={'National'} />
                          )}
                        </TableCell>
                      )}
                      <TableCell css={classes.date}>{formatDate(plan.createdDate, config.dateFormat)}</TableCell>
                      <TableCell>
                        {[plan.createdBy.firstName, plan.createdBy.lastName].filter(Boolean).join(' ')}
                      </TableCell>
                      <TableCell>
                        {(plan.access.edit || plan.access.download) && (
                          <Tooltip
                            title={
                              <Box display="flex" alignItems="center" gap={2} sx={{ wordBreak: 'break-word' }}>
                                <ArticleOutlinedIcon color="primary" />
                                {plan.file.name}
                              </Box>
                            }
                            placement="top-end"
                          >
                            <InfoOutlinedIcon color="primary" sx={{ display: 'block' }} />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        {(plan.access.edit || plan.access.download) && (
                          <IconButton css={classes.downloadBtn} onClick={createHandleDownload(plan.id)}>
                            <FileDownloadOutlinedIcon />
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell>
                        {plan.access.edit && (
                          <ConfirmDialog
                            title={formatMessage({
                              id: 'common.confirmTitle',
                              defaultMessage: 'Please, confirm the action!',
                            })}
                            description={formatMessage(
                              {
                                id: 'planDeletionConfirm.description',
                                defaultMessage: 'Delete "{plan}"?',
                              },
                              { plan: plan.type.name },
                            )}
                            onClose={createHandleDelete(plan.id)}
                          >
                            {({ setOpen }) => (
                              <IconButton css={classes.downloadBtn} onClick={() => setOpen(true)}>
                                <DeleteOutlineIcon />
                              </IconButton>
                            )}
                          </ConfirmDialog>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Box mt={4}>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={tablePagination.count}
                  rowsPerPage={tablePagination.limit}
                  page={tablePagination.page}
                  onPageChange={tablePagination.handlePageChange}
                  onRowsPerPageChange={tablePagination.handleRowsPerPageChange}
                />
              </Box>
            </Box>
          )}
        </Box>
      </AuthenticatedTemplate>
    </CommonPageLayout>
  );
};

const useStyles = (theme: Theme): UseStyles => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(6),
  },
  date: {
    color: theme.palette.custom.primary.grey[120],
  },
  downloadBtn: {
    backgroundColor: theme.palette.custom.primary.brand[20],
    height: 44,
    width: 44,
    color: theme.palette.primary.main,
  },
  filters: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    padding: theme.spacing(2, 6),
    gap: `${theme.spacing(2)} ${theme.spacing(6)}`,
    background: theme.palette.custom.primary.grey[50],
    borderRadius: 8,
  },
  filterLabel: {
    fontWeight: 500,
    whiteSpace: 'nowrap',
  },
  filter: {
    maxWidth: 200,
  },
});

export default UploadsPage;
